/**
 * SEPET AĞIRLIKLARI — kullanıcının yüzde olarak girdiği oranlar (13.10.2026).
 *
 * Yüzde EN FAZLA İKİ ONDALIKLA girilir ("33,33"). İçeride yüzün yüzde biri
 * (on binde bir) TAM SAYI olarak tutulur: %100 = 10 000. Toplam, eşit dağıtma
 * ve 100'e ölçekleme bu tam sayılarla yapılır; kayan noktalı bölme yok, yani
 * "toplam tam %100" sorusu yuvarlama artığına takılmaz.
 *
 * Motor ağırlığı KESİR ister ("0.4"); `kesirMetni` çevirir. Toplamı tam %100
 * olmayan sepet motora gitmez (motor da reddeder).
 */

export const YUZ = 10_000;

const DESEN = /^\d{1,3}(?:[.,]\d{1,2})?$/;

/** "40" → 4000, "33,33" → 3333; geçersiz ya da %100'ün üstü → null. */
export function yuzdeOku(metin: string): number | null {
  const t = metin.trim();
  if (!DESEN.test(t)) return null;
  const [tam = "0", kesir = ""] = t.replace(",", ".").split(".");
  const v = Number(tam) * 100 + Number(kesir.padEnd(2, "0"));
  return v > YUZ ? null : v;
}

/** 3333 → "33,33", 4000 → "40". Ekranda gösterilen biçim. */
export function yuzdeYaz(v: number): string {
  const tam = Math.floor(v / 100);
  const kesir = v % 100;
  if (kesir === 0) return String(tam);
  return `${tam},${String(kesir).padStart(2, "0").replace(/0$/, "")}`;
}

/** 3333 → "0.3333" (motorun beklediği kesir). */
export function kesirMetni(v: number): string {
  if (v === YUZ) return "1";
  const govde = String(v).padStart(4, "0").replace(/0+$/, "");
  return govde === "" ? "0" : `0.${govde}`;
}

/** Hepsi geçerliyse toplam; biri bile geçersizse null. */
export function toplam(metinler: readonly string[]): number | null {
  let t = 0;
  for (const m of metinler) {
    const v = yuzdeOku(m);
    if (v === null) return null;
    t += v;
  }
  return t;
}

/** Eşit dağıt: artan on binde birler baştakilere birer birer. */
export function esitDagit(n: number): string[] {
  if (n <= 0) return [];
  const taban = Math.floor(YUZ / n);
  const artan = YUZ - taban * n;
  return Array.from({ length: n }, (_, i) => yuzdeYaz(taban + (i < artan ? 1 : 0)));
}

/**
 * Toplamı %100'e ölçekler, oranları koruyarak. Taban kesilir, artan birimler
 * en büyük kesir artığından başlayarak dağıtılır (en büyük kalan yöntemi):
 * sonuç her zaman TAM %100. Toplam sıfırsa ya da geçersiz giriş varsa null.
 */
export function yuzeOlcekle(metinler: readonly string[]): string[] | null {
  const v = metinler.map(yuzdeOku);
  if (v.some((x) => x === null)) return null;
  const sayilar = v as number[];
  const t = sayilar.reduce((a, b) => a + b, 0);
  if (t === 0) return null;
  const tabanlar = sayilar.map((x) => Math.floor((x * YUZ) / t));
  const kalanlar = sayilar.map((x, i) => ({ i, k: (x * YUZ) % t }));
  let artan = YUZ - tabanlar.reduce((a, b) => a + b, 0);
  kalanlar.sort((a, b) => b.k - a.k || a.i - b.i);
  for (const { i } of kalanlar) {
    if (artan <= 0) break;
    tabanlar[i] = (tabanlar[i] ?? 0) + 1;
    artan -= 1;
  }
  return tabanlar.map(yuzdeYaz);
}

/**
 * Motorun 4 haneli kesri → yüzde metni: "0.3334" → "33,34", "1" → "100"
 * (sepeti hesaplardan doldur, 15.10.2026). Tanınmayan biçimde null.
 */
export function kesirdenYuzde(kesir: string): string | null {
  if (kesir === "1") return yuzdeYaz(YUZ);
  const m = /^0(?:\.(\d{1,4}))?$/.exec(kesir);
  if (m === null) return null;
  return yuzdeYaz(Number((m[1] ?? "").padEnd(4, "0")));
}
